import { Observable, fromEvent, merge } from "rxjs";

class Menu {
    private _localeTriggers: NodeListOf<HTMLElement>;
    private _localePanel: HTMLDivElement;
    private _megaTabs: NodeListOf<HTMLElement>;

    private _isLocalePanelVisible: boolean;
    private _openMegaTab: HTMLElement | null;
    private _lastLocaleTrigger: HTMLElement | null;

    init() {
        // Multiple entry points open the same shared panel: the desktop
        // header button, the homepage's mobile "All Locales" pill, and the
        // mobile-only caret next to "All {locale}" on locale/term pages.
        this._localeTriggers = document.querySelectorAll(".locale-switch-trigger");
        this._localePanel = document.querySelector("#locale-panel");

        if (this._localeTriggers.length && this._localePanel) {
            let localeTriggerObservable$: Observable<Event> | null = null;

            this._localeTriggers.forEach((trigger) => {
                const click$ = fromEvent(trigger, "click");
                localeTriggerObservable$ = localeTriggerObservable$ ? merge(localeTriggerObservable$, click$) : click$;
            });

            localeTriggerObservable$.subscribe((e) => this.toggleLocalePanel(e));
        }

        this._megaTabs = document.querySelectorAll(".facet-tab.has-mega");
        this._megaTabs.forEach((tab) => {
            tab.addEventListener("click", (e) => this.toggleMegaMenu(e, tab));
            // Tabbing out of an open menu closes it, so keyboard focus never
            // ends up somewhere else on the page with a menu still hanging
            // open over it. Only when focus actually lands somewhere
            // (relatedTarget set) — a mouse press on a non-focusable spot
            // inside the menu also blurs the trigger, with a null
            // relatedTarget, and must not close it.
            tab.addEventListener("focusout", (e: FocusEvent) => {
                const next = e.relatedTarget as Node | null;
                if (next && this._openMegaTab === tab && !tab.contains(next)) {
                    this.closeMegaMenus();
                }
            });
        });

        document.addEventListener("keydown", (e) => this.handleKeydown(e));

        this.revealActiveTab();
    }

    // Below 700px the facet bar is a sideways scroller (header.scss); on a
    // term page the active tab (e.g. Michelin) can start out past the right
    // edge. Scroll just that row — not the page — so it's in view.
    private revealActiveTab() {
        const scroller = document.querySelector<HTMLElement>(".facet-tabs");
        const active = scroller?.querySelector<HTMLElement>(".facet-tab.active");
        if (!scroller || !active || scroller.scrollWidth <= scroller.clientWidth) {
            return;
        }

        const overflowRight = active.offsetLeft + active.offsetWidth - (scroller.scrollLeft + scroller.clientWidth);
        if (overflowRight > 0) {
            scroller.scrollLeft += overflowRight + 32; // clear the 2rem edge fade
        }
    }

    // Esc closes whichever panel is open and puts focus back on the control
    // that opened it — the standard disclosure pattern for keyboard users.
    private handleKeydown(e: KeyboardEvent) {
        if (e.key !== "Escape") {
            return;
        }

        if (this._openMegaTab) {
            const trigger = this._openMegaTab.querySelector<HTMLElement>(".facet-trigger");
            this.closeMegaMenus();
            trigger?.focus();
        } else if (this._isLocalePanelVisible) {
            const trigger = this._lastLocaleTrigger;
            this.closeLocalePanel();
            trigger?.focus();
        }
    }

    // The locale panel and mega-menus are mutually exclusive — opening
    // either closes the other first. Without this,
    // e.g. opening "Areas" while the locale panel is already open leaves
    // both in the DOM at once, and since .locale-panel has a higher z-index
    // than .mega, the mega-menu renders visually stuck underneath it.
    toggleLocalePanel(e?: Event) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (this._isLocalePanelVisible) {
            this.closeLocalePanel();
        } else {
            this.closeMegaMenus();
            this._isLocalePanelVisible = true;
            if (e?.currentTarget instanceof HTMLElement) {
                this._lastLocaleTrigger = e.currentTarget;
                this.positionLocalePanel(e.currentTarget);
            }
            this._localePanel.classList.add("open");
            this._localeTriggers.forEach((trigger) => trigger.setAttribute("aria-expanded", "true"));
        }
    }

    // .locale-panel is a direct child of header.site rather than of whichever
    // trigger opened it (see the comment on _localeTriggers above), so unlike
    // .mega — which is a child of its own .facet-tab and sits flush beneath
    // it for free — its "top: 100%" CSS default measures from the bottom of
    // the entire multi-row header, not the trigger's own row. That leaves it
    // floating well below the clicked trigger with a large gap. Position it
    // explicitly from the trigger's actual rect instead.
    positionLocalePanel(trigger: HTMLElement) {
        const header = this._localePanel.parentElement;
        if (!header) {
            return;
        }

        const headerRect = header.getBoundingClientRect();
        const triggerRect = trigger.getBoundingClientRect();

        this._localePanel.style.top = `${triggerRect.bottom - headerRect.top + 8}px`;

        // Below 700px the panel spans the full header width (left/right: 0
        // in CSS) regardless of the trigger's position, so leave left alone
        // there — an inline style would otherwise win over that media query.
        if (window.matchMedia("(min-width: 43.75rem)").matches) {
            this._localePanel.style.left = `${triggerRect.left - headerRect.left}px`;
        } else {
            this._localePanel.style.left = "";
        }
    }

    closeLocalePanel() {
        if (!this._isLocalePanelVisible) {
            return;
        }

        this._isLocalePanelVisible = false;
        this._localePanel.classList.remove("open");
        this._localeTriggers.forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
    }

    toggleMegaMenu(e: Event, tab: HTMLElement) {
        const target = e.target as HTMLElement;
        const mega = tab.querySelector(".mega");

        // The dropdown is a child of the tab, so clicks on its real links
        // bubble through this same listener — let those navigate normally
        // instead of toggling (and hiding) the menu out from under them.
        if (mega && mega.contains(target)) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const alreadyOpen = this._openMegaTab === tab;

        this.closeMegaMenus();

        if (!alreadyOpen) {
            this.closeLocalePanel();
            tab.classList.add("open");
            tab.querySelector(".facet-trigger")?.setAttribute("aria-expanded", "true");
            this._openMegaTab = tab;
        }
    }

    closeMegaMenus() {
        this._megaTabs.forEach((tab) => {
            tab.classList.remove("open");
            tab.querySelector(".facet-trigger")?.setAttribute("aria-expanded", "false");
        });
        this._openMegaTab = null;
    }

    // Closes open panels only when the click landed outside them — a click
    // on a real link *inside* an open panel must be left alone, otherwise
    // hiding the panel mid-click (display: none) cancels the link's default
    // navigation before the browser commits to it.
    handleOutsideClick(target: Node) {
        if (this._isLocalePanelVisible && this._localePanel && !this._localePanel.contains(target)) {
            this.closeLocalePanel();
        }

        if (this._openMegaTab && !this._openMegaTab.contains(target)) {
            this.closeMegaMenus();
        }
    }
}

export default new Menu();
